import React, { useState, useEffect, useCallback } from 'react'
import * as api from './service/studyService'
import { PHASE_PATH } from './StudyPage'

const HIDE_KEY = 'study_nav_hidden'

// TEMPORARY back/next controls, pinned to the bottom of every study screen.
//
// The study is forward-only by design — that is what makes step timings and ordering
// interpretable — so this deliberately bypasses the normal advance path: no answer is
// recorded and no milestone is set. It exists so the interface can be walked and
// screenshotted without restarting, and it should be removed before participants run.
//
// Collapsible, because a control bar sitting over the interface ends up in every
// screenshot the control bar exists to help take. Hidden, it leaves only a corner
// handle; the choice persists across reloads, which matters here since every move
// is a full page load.
export default function StudyNav ({ pid, step, onRefresh }) {
  const [busy, setBusy] = useState(false)
  const [hidden, setHidden] = useState(() => localStorage.getItem(HIDE_KEY) === '1')

  const toggle = useCallback(() => setHidden(h => {
    const next = !h
    localStorage.setItem(HIDE_KEY, next ? '1' : '0')
    return next
  }), [])

  // Keyboard escape hatch, so the bar is never unrecoverable if the handle is missed.
  useEffect(() => {
    const onKey = (e) => {
      // Alt+Shift+N. e.code, not e.key — Option+Shift on macOS rewrites e.key to a
      // dead character, so matching on e.key never fires here.
      if (e.altKey && e.shiftKey && e.code === 'KeyN') { e.preventDefault(); toggle() }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [toggle])

  const move = async (delta) => {
    if (busy) return
    setBusy(true)
    try {
      const next = await api.navStep(pid, delta)
      // A step change can cross into a different route (modal ↔ /chi task), and the IDE
      // holds per-task state — editor contents, chat, test results — that must not carry
      // over. A full load is the only way to be sure none of it survives the move.
      const target = PHASE_PATH[next.phase] || '/study'
      if (window.location.pathname !== target) window.location.href = target
      else { await onRefresh(); window.location.reload() }
    } catch (e) {
      setBusy(false)
    }
  }

  if (hidden) {
    return (
      <button className="study-nav__peek" onClick={toggle}
        title="Show back/next controls (Alt+Shift+N)" aria-label="Show back/next controls">▸</button>
    )
  }

  const atStart = step?.index === 0
  const atEnd = step?.index != null && step?.total != null && step.index >= step.total - 1

  return (
    <div className="study-nav">
      <button className="study-nav__btn" onClick={() => move(-1)} disabled={busy || atStart}>← Back</button>
      {step?.index != null && step?.total != null && (
        <span className="study-nav__pos">{step.index + 1} / {step.total}</span>
      )}
      <button className="study-nav__btn" onClick={() => move(1)} disabled={busy || atEnd}>Next →</button>
      <button className="study-nav__hide" onClick={toggle} title="Hide (Alt+Shift+N)" aria-label="Hide controls">▾</button>
    </div>
  )
}
